import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import { PATH_DASHBOARD } from 'src/routes/paths';

// Shown when the lead 404s on the active-lead endpoint — almost always because it was just archived.
// Rendered inline (rather than auto-closing) so the user can see what happened and open the archived record.
export default function ArchivedLeadEmptyState({ leadId, practiceId, embedded, onClose }) {
    const navigate = useNavigate();

    const onViewArchivedLead = () => {
        if (practiceId && leadId) {
            navigate(
                PATH_DASHBOARD.general.archiveDetail
                    .replace(':practiceId', practiceId)
                    .replace(':id', leadId)
            );
        } else {
            navigate(PATH_DASHBOARD.tables.archiveLeadTable);
        }
    };

    return (
        <Box
            data-testid="lead-archived-empty-state"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 6,
                textAlign: 'center',
                minHeight: embedded ? 280 : '60vh',
            }}
        >
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255,86,48,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="mdi:archive-outline" width={32} height={32} sx={{ color: '#FF5630' }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', mb: 0.5 }}>
                    This lead has been archived
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#6e6e73' }}>
                    The lead no longer appears in active tables. You can still find it in the archive.
                </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
                <Button
                    data-testid="lead-archived-go-to-archive"
                    variant="contained"
                    disableElevation
                    startIcon={<Iconify icon="mdi:archive-arrow-down-outline" width={16} height={16} />}
                    onClick={onViewArchivedLead}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#FF5630',
                        '&:hover': { bgcolor: '#d9472b' },
                        boxShadow: 'none',
                    }}
                >
                    View archived lead
                </Button>
                <Button
                    data-testid="lead-archived-close"
                    variant="outlined"
                    onClick={() => onClose?.()}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Close
                </Button>
            </Stack>
        </Box>
    );
}

ArchivedLeadEmptyState.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    embedded: PropTypes.bool,
    onClose: PropTypes.func,
};
